/**
 * batch-71 workflow-sjablonen (FO-533 §A2) - moment en deadline van een stap, in
 * Europe/Amsterdam.
 *
 * Een dag is een dag-precisie Date: UTC-middernacht van de NL-kalenderdag, zoals
 * `OperationeleTaak.deadline` is opgeslagen. Werkdagen via `isWerkdag` (ma-vr, geen
 * feestdag); feestdagen komen als `yyyy-MM-dd` van de aanroeper (`laadFeestdagen`),
 * zodat dit puur blijft.
 */
import type { WorkflowOffsetEenheid, WorkflowWerkdagCorrectie } from '@streep/types'
import { contractDagPlusDagen, contractDagPlusMaanden } from '../contract-mijlpalen'
import { formatDagDatum, parseDagDatum, vandaagAmsterdam } from '../dagtaken-datum'
import { isWerkdag } from '../uren-calculations'
import { amsterdamWallclockToUtc } from '../amsterdam-tijd'

function feestdagenAlsDatums(feestdagen: readonly string[]): Date[] {
  const uit: Date[] = []
  for (const f of feestdagen) {
    try {
      uit.push(parseDagDatum(f))
    } catch {
      // Een ongeldige feestdag mag de deadline niet breken.
    }
  }
  return uit
}

/** De NL-kalenderdag van een moment, als `yyyy-MM-dd`. */
export function workflowDag(moment: Date): string {
  return formatDagDatum(vandaagAmsterdam(moment))
}

/**
 * De dag `offsetWerkdagen` werkdagen na `start` (NL-kalenderdag). 0 = de eerste
 * werkdag op of na `start`: een taak die in het weekend opengaat heeft zijn deadline
 * niet in dat weekend. Geeft UTC-middernacht van de NL-dag terug.
 */
export function volgendeDeadline(offsetWerkdagen: number, start: Date = new Date(), feestdagen: readonly string[] = []): Date {
  const vrij = feestdagenAlsDatums(feestdagen)
  const doel = Math.max(0, Math.floor(offsetWerkdagen))
  let dag = workflowDag(start)
  // Plafond: een jaar werkdagen is ruim genoeg, en een fout in de invoer hangt nooit.
  for (let i = 0; i < 366 && !isWerkdag(parseDagDatum(dag), vrij); i++) dag = contractDagPlusDagen(dag, 1)
  let geteld = 0
  for (let i = 0; i < 1500 && geteld < doel; i++) {
    dag = contractDagPlusDagen(dag, 1)
    if (isWerkdag(parseDagDatum(dag), vrij)) geteld += 1
  }
  return parseDagDatum(dag)
}

/** `anker` (NL-dag) plus een offset, in de gegeven eenheid. */
export function workflowDagPlus(
  ankerDag: string,
  aantal: number,
  eenheid: WorkflowOffsetEenheid,
  feestdagen: readonly string[] = [],
): string {
  if (eenheid === 'WERKDAGEN') return formatDagDatum(volgendeDeadline(aantal, parseDagDatum(ankerDag), feestdagen))
  if (aantal === 0) return ankerDag
  if (eenheid === 'MAANDEN') return contractDagPlusMaanden(ankerDag, aantal)
  return contractDagPlusDagen(ankerDag, eenheid === 'WEKEN' ? aantal * 7 : aantal)
}

function pasCorrectieToe(dag: string, correctie: WorkflowWerkdagCorrectie, feestdagen: readonly string[]): string {
  if (correctie === 'GEEN') return dag
  const vrij = feestdagenAlsDatums(feestdagen)
  const stap = correctie === 'VORIGE_WERKDAG' ? -1 : 1
  let huidig = dag
  for (let i = 0; i < 31 && !isWerkdag(parseDagDatum(huidig), vrij); i++) huidig = contractDagPlusDagen(huidig, stap)
  return huidig
}

export interface StapMomentInvoer {
  /** Waarop het moment rekent (de start van de run of "nu"). */
  anker: Date
  offsetAantal: number
  offsetEenheid: WorkflowOffsetEenheid
  werkdagCorrectie: WorkflowWerkdagCorrectie
  tijdstip: string | null
  voorloopDagen: number
  feestdagen?: readonly string[]
}

/**
 * Het moment waarop een stap op zijn vroegst opengaat. Null = direct (geen offset):
 * de stap volgt dan alleen zijn voorgangers. Anders `anker + offset`, op een werkdag
 * als de correctie dat vraagt, `voorloopDagen` eerder open, om `tijdstip` NL-tijd
 * (of om middernacht NL-tijd).
 */
export function berekenStapOpentOp(i: StapMomentInvoer): Date | null {
  if (i.offsetAantal <= 0 && i.voorloopDagen <= 0 && !i.tijdstip) return null
  const feestdagen = i.feestdagen ?? []
  const moment = pasCorrectieToe(
    workflowDagPlus(workflowDag(i.anker), i.offsetAantal, i.offsetEenheid, feestdagen),
    i.werkdagCorrectie,
    feestdagen,
  )
  const open = i.voorloopDagen > 0 ? contractDagPlusDagen(moment, -i.voorloopDagen) : moment
  return amsterdamWallclockToUtc(open, i.tijdstip ?? '00:00')
}

export interface StapDeadlineInvoer {
  /** De stap gaat open (of het moment van de start, bij anker RUN_START). */
  ankerOpen: Date
  ankerStart: Date
  deadlineAnker: 'RUN_START' | 'VORIGE_STAP' | null
  deadlineOffsetAantal: number
  deadlineOffsetEenheid: WorkflowOffsetEenheid
  termijnDagen: number | null
  feestdagen?: readonly string[]
}

/**
 * De deadline van een stap (UTC-middernacht van de NL-dag), of null. Een eigen anker
 * plus offset wint van `termijnDagen`, zoals bij de contractmijlpalen.
 */
export function berekenStapDeadline(i: StapDeadlineInvoer): Date | null {
  const feestdagen = i.feestdagen ?? []
  if (i.deadlineAnker) {
    const anker = i.deadlineAnker === 'RUN_START' ? i.ankerStart : i.ankerOpen
    if (i.deadlineOffsetEenheid === 'WERKDAGEN') return volgendeDeadline(i.deadlineOffsetAantal, anker, feestdagen)
    return parseDagDatum(workflowDagPlus(workflowDag(anker), i.deadlineOffsetAantal, i.deadlineOffsetEenheid, feestdagen))
  }
  if (i.termijnDagen !== null) return parseDagDatum(contractDagPlusDagen(workflowDag(i.ankerOpen), i.termijnDagen))
  return null
}

/** Is de deadline verstreken? De deadline-dag zelf telt nog als op tijd (NL-tijd). */
export function isStapVerlopen(deadline: Date | null, nu: Date = new Date()): boolean {
  if (!deadline) return false
  return vandaagAmsterdam(nu).getTime() > deadline.getTime()
}

/** Hoeveel werkdagen na `sinds` is het vandaag? (0 op de dag zelf en in het weekend erna.) */
export function werkdagenSindsMoment(sinds: Date, nu: Date = new Date(), feestdagen: readonly string[] = []): number {
  const vrij = feestdagenAlsDatums(feestdagen)
  const tot = workflowDag(nu)
  let dag = workflowDag(sinds)
  let geteld = 0
  for (let i = 0; i < 1500 && dag < tot; i++) {
    dag = contractDagPlusDagen(dag, 1)
    if (isWerkdag(parseDagDatum(dag), vrij)) geteld += 1
  }
  return geteld
}
